import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, Download, Smartphone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const FooterCTA = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will connect to backend later
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" ref={ref} className="relative overflow-hidden">
      {/* App Download Section */}
      <div className="py-32 px-4 sm:px-6 lg:px-8 bg-black relative overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              <span className="text-blue-400">SupaDoc AI</span>
              <br />
              Coming Soon
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Get instant medical triage, symptom analysis, and healthcare guidance 24/7
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.02, rotateY: 5, z: 50 }}
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12 relative overflow-hidden"
            style={{ transformStyle: "preserve-3d" }}
          >
            {/* Animated Background Pattern */}
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{ 
                backgroundPosition: ['0% 0%', '100% 100%'],
              }}
              transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
              style={{
                backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
                backgroundSize: '30px 30px'
              }}
            />

            <div className="relative z-10">
              <div className="text-center mb-12">
                <motion.div
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, 0, -5, 0]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="inline-block mb-6"
                >
                  <Smartphone className="w-20 h-20 text-blue-400" />
                </motion.div>
              </div>

              <div className="flex flex-col items-center justify-center mb-10">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/10 backdrop-blur-md text-white/60 font-bold text-lg px-12 py-8 rounded-2xl shadow-xl border-2 border-white/20 flex items-center gap-3 cursor-not-allowed"
                >
                  <Download className="w-6 h-6 opacity-50" />
                  App Coming Soon
                </motion.div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap justify-center gap-6">
                {['Free 10 Messages', '24/7 AI Support', 'Instant Triage', '3-8 sec Response'].map((feature, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 px-6 py-3 rounded-full flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                    <span className="text-white font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-32 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-6 text-black leading-tight">
              Future of Emergency Healthcare
              <br />
              is <span className="text-blue-600">Coordination,</span> Not Chaos
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.02, rotateY: 5 }}
              className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-10 shadow-2xl"
              style={{ transformStyle: "preserve-3d" }}
            >
              <h3 className="text-3xl font-bold mb-8 text-gray-900">
                Get in Touch
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Name</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl border-gray-300 focus:border-blue-500 focus:ring-blue-500 py-6"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Email</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl border-gray-300 focus:border-blue-500 focus:ring-blue-500 py-6"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Phone</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-xl border-gray-300 focus:border-blue-500 focus:ring-blue-500 py-6"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Message</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full rounded-xl border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Tell us about your needs..."
                  />
                </div>
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-7 rounded-xl text-lg shadow-lg flex items-center justify-center gap-2 group"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
              </form>
            </motion.div>

            {/* Contact Info Cards */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              <motion.div
                whileHover={{ scale: 1.03, rotateY: -5, z: 30 }}
                className="bg-blue-600 rounded-3xl p-10 shadow-2xl text-white"
                style={{ transformStyle: "preserve-3d" }}
              >
                <Phone className="w-12 h-12 mb-4" />
                <h4 className="text-2xl font-bold mb-3">Phone</h4>
                <a href="tel:+918875024908" className="text-xl hover:text-blue-200 transition-colors">
                  +91 8875024908
                </a>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.03, rotateY: -5, z: 30 }}
                className="bg-black rounded-3xl p-10 shadow-2xl text-white"
                style={{ transformStyle: "preserve-3d" }}
              >
                <Mail className="w-12 h-12 mb-4" />
                <h4 className="text-2xl font-bold mb-3">Email</h4>
                <a href="mailto:contact@supaaid.in" className="text-xl hover:text-gray-300 transition-colors">
                  contact@supaaid.in
                </a>
              </motion.div>
            </motion.div>
          </div>

          {/* Legal Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-16"
          >
            <p className="text-sm text-gray-500 italic">
              "I am an AI interface. For confirmed diagnosis, consult licensed professionals."
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FooterCTA;
